import React, { useState } from "react";
import { supabase } from "./supabase";
import "./App.css";
import lockupStacked from "./assets/brand/lockup-stacked.svg";
import { GOOGLE_AUTH_ENABLED } from "./featureFlags";

export default function Signup({ onSuccess, onLogin }) {
  // "email" (check invite status) -> "form" (create the account).
  // Nothing is created in Supabase Auth until step "form" succeeds, so an
  // unapproved email never leaves a stray auth.users row behind.
  const [step, setStep] = useState("email");
  const [checkEmail, setCheckEmail] = useState("");
  const [checkingInvite, setCheckingInvite] = useState(false);
  const [inviteBlocked, setInviteBlocked] = useState(false);

  const [form, setForm] = useState({
    email: "",
    password: "",
    confirm: "",
    full_name: "",
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  async function handleCheckInvite() {
    setError(null);
    const normalized = checkEmail.trim().toLowerCase();
    if (!normalized) {
      setError("Please enter your email address.");
      return;
    }

    setCheckingInvite(true);
    // check_waitlist_invited is a SECURITY DEFINER RPC (no session exists
    // yet at this point) that returns only a boolean — approved iff a
    // waitlist_applications row for this email has status = 'invited',
    // set manually via Table Editor when an application is reviewed.
    const { data: invited, error: rpcError } = await supabase.rpc(
      "check_waitlist_invited",
      { p_email: normalized },
    );
    setCheckingInvite(false);

    if (rpcError) {
      setError(
        "Something went wrong checking your invite status. Please try again.",
      );
      return;
    }

    if (!invited) {
      setInviteBlocked(true);
      return;
    }

    setForm((prev) => ({ ...prev, email: normalized }));
    setStep("form");
  }

  async function handleSignup() {
    setError(null);
    if (!form.full_name.trim()) {
      setError("Please enter your full name.");
      return;
    }
    if (form.password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (form.password !== form.confirm) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);

    const { data, error: signUpError } = await supabase.auth.signUp({
      email: form.email,
      password: form.password,
      options: { data: { full_name: form.full_name.trim() } },
    });

    if (signUpError) {
      if (signUpError.message.includes("already registered")) {
        setError(
          "An account with this email already exists. Please log in instead.",
        );
      } else {
        setError(signUpError.message);
      }
      setLoading(false);
      return;
    }

    if (!data?.user) {
      setError("Account creation failed. Please try again.");
      setLoading(false);
      return;
    }

    // Don't create profile here — create_store_with_owner RPC in Onboarding
    // handles profile creation with the correct store_id
    setLoading(false);
    onSuccess(data.user);
  }

  async function handleGoogleLogin() {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: window.location.origin,
      },
    });
    if (error) setError(error.message);
  }

  function retryWithDifferentEmail() {
    setInviteBlocked(false);
    setCheckEmail("");
    setError(null);
    setStep("email");
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        {/* Brand */}
        <div
          className="auth-brand"
          style={{ justifyContent: "center", marginBottom: "28px" }}
        >
          <img
            src={lockupStacked}
            alt="SuqPOS — Smart Sales, Anywhere"
            style={{ height: "110px", width: "auto" }}
          />
        </div>

        {inviteBlocked ? (
          <>
            <h2 className="auth-title">We couldn't find an approved invite</h2>
            <p className="auth-sub">
              We didn't find an approved early-access request for that
              email.
            </p>

            <div
              style={{
                background: "var(--elevated)",
                border: "1px solid var(--border)",
                borderRadius: "var(--radius-lg)",
                padding: "16px",
                marginTop: "20px",
                textAlign: "left",
              }}
            >
              <p
                style={{
                  fontSize: "13px",
                  color: "var(--text2)",
                  lineHeight: 1.6,
                }}
              >
                Submit a request for early access from the homepage, or
                make sure you're using the same email you applied with.
              </p>
            </div>

            <button
              className="auth-submit"
              style={{ marginTop: "20px" }}
              onClick={retryWithDifferentEmail}
            >
              Try a different email
            </button>

            <p className="auth-switch" style={{ marginTop: "12px" }}>
              <button className="auth-switch-btn" onClick={onLogin}>
                ← Back to login
              </button>
            </p>
          </>
        ) : step === "email" ? (
          <>
            <h2 className="auth-title">Enter your invite email</h2>
            <p className="auth-sub">
              SuqPOS is currently invite-only. Enter the email address you
              used to request early access.
            </p>

            {error && <div className="auth-error">⚠ {error}</div>}

            <div className="auth-form">
              <div className="auth-field">
                <label>Email Address</label>
                <input
                  type="email"
                  placeholder="you@example.com"
                  value={checkEmail}
                  onChange={(e) => setCheckEmail(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleCheckInvite()}
                />
              </div>

              <button
                className="auth-submit"
                onClick={handleCheckInvite}
                disabled={checkingInvite}
              >
                {checkingInvite ? "Checking..." : "Continue →"}
              </button>
            </div>

            <p className="auth-switch">
              Already have an account?{" "}
              <button className="auth-switch-btn" onClick={onLogin}>
                Log in
              </button>
            </p>
          </>
        ) : (
          <>
            <h2 className="auth-title">Create your account</h2>
            <p className="auth-sub">
              Start your 3-month free trial. No credit card required.
            </p>

            {error && <div className="auth-error">⚠ {error}</div>}

            <div className="auth-form">
              {GOOGLE_AUTH_ENABLED && (
                <>
                  {/* Google Sign In */}
                  <button
                    type="button"
                    className="google-auth-btn"
                    onClick={handleGoogleLogin}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24">
                      <path
                        fill="#4285F4"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="#34A853"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.70 23 12 23z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
                      />
                      <path
                        fill="#EA4335"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                    Continue with Google
                  </button>

                  <div className="auth-divider">
                    <span>or</span>
                  </div>
                </>
              )}

              <div className="auth-field">
                <label>Email Address</label>
                <input
                  type="email"
                  value={form.email}
                  disabled
                  style={{ opacity: 0.7, cursor: "not-allowed" }}
                />
              </div>
              <div className="auth-field">
                <label>Full Name</label>
                <input
                  type="text"
                  placeholder="e.g. Tigist Haile"
                  value={form.full_name}
                  onChange={(e) =>
                    setForm({ ...form, full_name: e.target.value })
                  }
                  onKeyDown={(e) => e.key === "Enter" && handleSignup()}
                />
              </div>
              <div className="auth-field">
                <label>Password</label>
                <input
                  type="password"
                  placeholder="Min. 6 characters"
                  value={form.password}
                  onChange={(e) =>
                    setForm({ ...form, password: e.target.value })
                  }
                  onKeyDown={(e) => e.key === "Enter" && handleSignup()}
                />
              </div>
              <div className="auth-field">
                <label>Confirm Password</label>
                <input
                  type="password"
                  placeholder="Repeat your password"
                  value={form.confirm}
                  onChange={(e) =>
                    setForm({ ...form, confirm: e.target.value })
                  }
                  onKeyDown={(e) => e.key === "Enter" && handleSignup()}
                />
              </div>

              <button
                className="auth-submit"
                onClick={handleSignup}
                disabled={loading}
              >
                {loading ? "Creating account..." : "Create account →"}
              </button>
            </div>

            <p className="auth-switch">
              Already have an account?{" "}
              <button className="auth-switch-btn" onClick={onLogin}>
                Log in
              </button>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
